/**
 * ScoreProjector — Interactive ROI projection calculator for the results page.
 *
 * Shows what closing score gaps could mean for the user's business in terms of:
 * 1. Time recaptured (hours/week)
 * 2. Client quality (qualitative)
 * 3. Revenue added ($/month and $/year)
 *
 * Math model calibrated from TSB VOC data (~43K contacts, 5 survey sources).
 * All projections trace to documented data points. Conservative by default.
 */

import { useState, useMemo } from 'react';
import type { SubmissionScores, Answers, Section } from '../types';

// ─── Pillar math model ───────────────────────────────────────────────────

interface PillarModel {
  label: string;
  baseValuePerPoint: number;  // $/mo at $3,500 base revenue
  hoursPerPoint: number;      // hours/week recaptured per point
  rampMonths: number;
  confidence: string;
  confidenceLabel: string;
  tooltip: string;
}

// Map scorecard section IDs to the data model pillars.
// Labels and tooltips match the actual Clients Scorecard pillars —
// no pricing-coaching language (that belongs in a different program).
const PILLAR_MODEL: Record<string, PillarModel> = {
  clarity: {
    label: 'Clarity',
    baseValuePerPoint: 625,
    hoursPerPoint: 3,
    rampMonths: 3,
    confidence: 'high',
    confidenceLabel: 'Based on 418 bookkeeper client-profile records',
    tooltip: 'Lock in exactly who you want as a client and what you\'re selling them. When your ideal client is clear, the right ones say yes faster — and the wrong ones stop taking up your time.',
  },
  pipeline: {
    label: 'Pipeline',
    baseValuePerPoint: 425,
    hoursPerPoint: 2,
    rampMonths: 5,
    confidence: 'high',
    confidenceLabel: 'Based on stated goals from 367 bookkeepers',
    tooltip: 'Build a predictable way to generate new client conversations — so lead flow stops being "busy one month, quiet the next." Each point forward is roughly one more right-fit client conversation per month.',
  },
  conversion: {
    label: 'Conversion',
    baseValuePerPoint: 375,
    hoursPerPoint: 4,
    rampMonths: 5,
    confidence: 'medium-high',
    confidenceLabel: 'Based on 444 survey responses on sales process gaps',
    tooltip: 'Close more of the conversations you\'re already having. A clear discovery + follow-up process means the right-fit prospects stop going quiet and start saying yes.',
  },
  visibility: {
    label: 'Visibility',
    baseValuePerPoint: 300,
    hoursPerPoint: 1,
    rampMonths: 7,
    confidence: 'medium',
    confidenceLabel: 'Based on self-reported marketing weakness data',
    tooltip: 'Get found by the right people — consistently. The lag is longer here (6-9 months), but the leads that come through are higher quality and lower effort.',
  },
  referrals: {
    label: 'Referrals',
    baseValuePerPoint: 175,
    hoursPerPoint: 1,
    rampMonths: 9,
    confidence: 'low-medium',
    confidenceLabel: 'Based on top-performer patterns in our community',
    tooltip: 'Turn your best clients into a growth engine. Top performers generate 60-80% of new clients through referrals — but it\'s a system, not luck.',
  },
};

// Priority order for tie-breaking
const PILLAR_PRIORITY = ['clarity', 'pipeline', 'conversion', 'visibility', 'referrals'];

// Belt framework
const BELTS = [
  { label: 'White Belt', min: 0, max: 100000, color: '#e5e7eb' },
  { label: 'Yellow Belt', min: 100000, max: 200000, color: '#fbbf24' },
  { label: 'Green Belt', min: 200000, max: 300000, color: '#34d399' },
  { label: 'Blue Belt', min: 300000, max: 400000, color: '#60a5fa' },
  { label: 'Red Belt', min: 400000, max: 500000, color: '#f87171' },
  { label: 'Black Belt', min: 500000, max: Infinity, color: '#1f2937' },
];

function getBelt(annualRevenue: number) {
  return BELTS.find(b => annualRevenue >= b.min && annualRevenue < b.max) ?? BELTS[0];
}

function getNextBelt(annualRevenue: number) {
  const idx = BELTS.findIndex(b => annualRevenue >= b.min && annualRevenue < b.max);
  return idx >= 0 && idx < BELTS.length - 1 ? BELTS[idx + 1] : null;
}

// ─── Component ───────────────────────────────────────────────────────────

interface Props {
  scores: SubmissionScores;
  sections: Section[];
  answers?: Answers;  // for pre-filling context inputs
}

export default function ScoreProjector({ scores, sections, answers }: Props) {
  // Pre-fill from context answers if available
  const defaultRevenue = answers?.['ctx-revenue'] as number | undefined;
  const defaultClients = answers?.['ctx-clients'] as number | undefined;

  // Baseline = the user's CURRENT state (what they entered during context questions).
  // This is the zero point for the "revenue left on the table" delta.
  const baselineRevenue = defaultRevenue ?? 3500;
  const baselineClients = defaultClients ?? 15;
  const baselineAvgPerClient = Math.max(
    100,
    Math.round(baselineRevenue / Math.max(baselineClients, 1)),
  );
  const baselineMonthly = baselineAvgPerClient * baselineClients;

  // Sliders control the "what-if" levers: average revenue per client and
  // client count. Monthly revenue is derived — no longer an input.
  const [avgPerClient, setAvgPerClient] = useState(baselineAvgPerClient);
  const [clientCount, setClientCount] = useState(baselineClients);

  const monthlyRevenue = avgPerClient * clientCount;
  const scaleFactor = monthlyRevenue / 3500;

  // Revenue they're walking past today: only positive when sliders go above baseline.
  const leftOnTableAnnual = Math.max(0, (monthlyRevenue - baselineMonthly) * 12);

  // Map section scores to pillar models
  const pillarData = useMemo(() => {
    if (!scores.sections) return [];
    return PILLAR_PRIORITY
      .filter(id => scores.sections![id])
      .map(id => {
        const s = scores.sections![id];
        const model = PILLAR_MODEL[id];
        const section = sections.find(sec => sec.id === id);
        // Normalize score to 1-5 scale (from section score)
        const currentNorm = s.max > 0 ? (s.score / s.max) * 5 : 0;
        const gapPoints = 5 - currentNorm;
        const monthlyValue = model.baseValuePerPoint * scaleFactor * gapPoints;
        // Hours recaptured scale with the size of the practice — bigger book + higher
        // per-client value means more systems gain when you close the gap. Dampened
        // by sqrt so it grows but doesn't explode.
        const hoursWeek = model.hoursPerPoint * gapPoints * Math.sqrt(Math.max(scaleFactor, 0.25));
        return {
          id,
          model,
          sectionLabel: section?.label ?? model.label,
          score: s.score,
          max: s.max,
          pct: s.max > 0 ? Math.round((s.score / s.max) * 100) : 0,
          currentNorm: Math.round(currentNorm * 10) / 10,
          gapPoints,
          monthlyValue,
          hoursWeek,
        };
      })
      .sort((a, b) => b.monthlyValue - a.monthlyValue);
  }, [scores.sections, sections, scaleFactor]);

  // Top lever (highest value gap)
  const topLever = pillarData[0];

  // Conservative: top lever only
  const conservativeMonthly = topLever ? Math.round(topLever.monthlyValue) : 0;
  const conservativeAnnual = conservativeMonthly * 12;

  // Full potential: all pillars × 0.7 blending
  const fullPotentialMonthly = Math.round(
    pillarData.reduce((sum, p) => sum + p.monthlyValue, 0) * 0.7
  );
  const fullPotentialAnnual = fullPotentialMonthly * 12;

  // Apply guardrails
  const cappedConservativeAnnual = Math.min(
    Math.max(conservativeAnnual, 5000),
    monthlyRevenue * 12
  );
  const cappedFullAnnual = Math.min(
    Math.max(fullPotentialAnnual, 5000),
    monthlyRevenue * 12
  );
  const cappedConservativeMonthly = Math.round(cappedConservativeAnnual / 12);
  const cappedFullMonthly = Math.round(cappedFullAnnual / 12);

  // Time recaptured
  const totalHoursWeek = Math.min(
    pillarData.reduce((sum, p) => sum + p.hoursWeek, 0),
    40
  );

  // Client quality statement — reflects the DIRECTION the user moved the sliders
  // relative to their baseline. Each axis has three states (up / same / down).
  const PRICE_THRESHOLD = 0.05;  // ±5% counts as "same"
  const CLIENT_THRESHOLD = 1;    // ±1 client counts as "same"
  const priceDir =
    avgPerClient > baselineAvgPerClient * (1 + PRICE_THRESHOLD) ? 'up'
    : avgPerClient < baselineAvgPerClient * (1 - PRICE_THRESHOLD) ? 'down'
    : 'same';
  const clientDir =
    clientCount > baselineClients + CLIENT_THRESHOLD ? 'up'
    : clientCount < baselineClients - CLIENT_THRESHOLD ? 'down'
    : 'same';
  const clientQuality = (() => {
    // Baseline state — no movement
    if (priceDir === 'same' && clientDir === 'same')
      return 'The same book — right-fit and referring more';
    // Growth on both axes
    if (priceDir === 'up'   && clientDir === 'up')
      return 'More right-fit clients at stronger rates';
    // Price up, clients same/down
    if (priceDir === 'up'   && clientDir === 'same')
      return 'Same clients — each one worth more to you';
    if (priceDir === 'up'   && clientDir === 'down')
      return 'Fewer, better-fit clients at stronger rates';
    // Clients up, price same/down
    if (priceDir === 'same' && clientDir === 'up')
      return 'A bigger book of right-fit clients';
    if (priceDir === 'down' && clientDir === 'up')
      return 'More clients — a higher-volume practice';
    // Contracting
    if (priceDir === 'same' && clientDir === 'down')
      return 'Fewer clients — more room to grow each one';
    if (priceDir === 'down' && clientDir === 'same')
      return 'Same book at lower tickets — a leaner practice';
    return 'A smaller, more focused practice';
  })();

  // Belt
  const currentAnnual = monthlyRevenue * 12;
  const currentBelt = getBelt(currentAnnual);
  const projectedAnnual = currentAnnual + cappedConservativeAnnual;
  const projectedBelt = getBelt(projectedAnnual);
  const nextBelt = getNextBelt(currentAnnual);

  // 12-month trajectory data (for mini chart)
  const trajectoryData = useMemo(() => {
    const months = Array.from({ length: 13 }, (_, i) => i);
    return months.map(m => {
      // Conservative: top lever ramp
      let consAdd = 0;
      if (topLever && m > 0) {
        const rampFactor = Math.min(m / topLever.model.rampMonths, 1);
        consAdd = Math.round(topLever.monthlyValue * rampFactor);
      }
      // Full potential: all pillars blended
      let fullAdd = 0;
      if (m > 0) {
        for (const p of pillarData) {
          const rampFactor = Math.min(m / p.model.rampMonths, 1);
          fullAdd += p.monthlyValue * rampFactor;
        }
        fullAdd = Math.round(fullAdd * 0.7);
      }
      return {
        month: m,
        conservative: monthlyRevenue + Math.min(consAdd, monthlyRevenue),
        full: monthlyRevenue + Math.min(fullAdd, monthlyRevenue),
      };
    });
  }, [pillarData, topLever, monthlyRevenue]);

  const chartMax = Math.max(...trajectoryData.map(d => d.full));
  const chartMin = monthlyRevenue * 0.9;
  const chartRange = chartMax - chartMin || 1;

  if (!scores.sections || pillarData.length === 0) return null;

  return (
    <div className="projector">
      <div className="projector-header">
        <h3 className="projector-title">What closing your gaps could mean</h3>
        <p className="projector-subtitle">
          You told us where you are now. Move the sliders to see where your practice could be — and what's waiting for you to go get it.
        </p>
      </div>

      {/* ── Anchor card: where you are now (baseline, read-only) ── */}
      <div className="projector-anchor">
        <div className="projector-anchor-label">You told us this is where you are</div>
        <div className="projector-anchor-row">
          <div className="projector-anchor-cell">
            <div className="projector-anchor-value">${baselineAvgPerClient.toLocaleString()}</div>
            <div className="projector-anchor-cell-label">per client</div>
          </div>
          <div className="projector-anchor-divider" aria-hidden="true">·</div>
          <div className="projector-anchor-cell">
            <div className="projector-anchor-value">{baselineClients}</div>
            <div className="projector-anchor-cell-label">clients</div>
          </div>
          <div className="projector-anchor-divider" aria-hidden="true">·</div>
          <div className="projector-anchor-cell">
            <div className="projector-anchor-value">${baselineMonthly.toLocaleString()}</div>
            <div className="projector-anchor-cell-label">per month</div>
          </div>
        </div>
      </div>

      {/* ── Input sliders ── */}
      <div className="projector-inputs-header">Move the sliders to see where you could be</div>
      <div className="projector-inputs">
        <div className="projector-input-group">
          <label className="projector-input-label">
            Average revenue per client
            <span className="projector-input-value">${avgPerClient.toLocaleString()}/client</span>
          </label>
          <input
            type="range"
            min={100}
            max={1500}
            step={25}
            value={avgPerClient}
            onChange={e => setAvgPerClient(Number(e.target.value))}
            className="projector-slider"
          />
          {avgPerClient < 150 && (
            <div className="projector-input-meta">
              <span className="projector-warning">
                Under $150/client is below the TSB community median — attracting better-fit clients raises this naturally.
              </span>
            </div>
          )}
        </div>
        <div className="projector-input-group">
          <label className="projector-input-label">
            Number of clients
            <span className="projector-input-value">{clientCount} clients</span>
          </label>
          <input
            type="range"
            min={1}
            max={60}
            step={1}
            value={clientCount}
            onChange={e => setClientCount(Number(e.target.value))}
            className="projector-slider"
          />
          <div className="projector-input-meta">
            Monthly revenue: <strong>${monthlyRevenue.toLocaleString()}/mo</strong> · ${(monthlyRevenue * 12).toLocaleString()}/yr
          </div>
        </div>
      </div>

      {/* ── Belt indicator ── */}
      <div className="projector-belt-row">
        <div className="projector-belt">
          <span className="projector-belt-dot" style={{ background: currentBelt.color }} />
          <span className="projector-belt-label">Now: {currentBelt.label}</span>
          <span className="projector-belt-revenue">${currentAnnual.toLocaleString()}/yr</span>
        </div>
        {nextBelt && projectedBelt !== currentBelt && (
          <>
            <span className="projector-belt-arrow">
              <svg width="20" height="12" viewBox="0 0 20 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 6h18M14 1l5 5-5 5"/></svg>
            </span>
            <div className="projector-belt">
              <span className="projector-belt-dot" style={{ background: projectedBelt.color }} />
              <span className="projector-belt-label">{projectedBelt.label}</span>
              <span className="projector-belt-revenue">${projectedAnnual.toLocaleString()}/yr</span>
            </div>
          </>
        )}
      </div>

      {/* ── Revenue left on the table ── */}
      {leftOnTableAnnual > 0 && (
        <div className="projector-left-on-table">
          <div className="projector-left-on-table-copy">
            <div className="projector-left-on-table-label">Revenue you're walking past today</div>
            <div className="projector-left-on-table-caption">
              If you priced at ${avgPerClient.toLocaleString()}/client with {clientCount} clients — starting from where you are now (${baselineAvgPerClient.toLocaleString()}/client × {baselineClients} = ${baselineMonthly.toLocaleString()}/mo).
            </div>
          </div>
          <div className="projector-left-on-table-value">+${leftOnTableAnnual.toLocaleString()}<span className="projector-left-on-table-unit">/yr</span></div>
        </div>
      )}

      {/* ── Three-column output ── */}
      <div className="projector-outcomes">
        <div className="projector-outcome">
          <div className="projector-outcome-value">+{Math.round(totalHoursWeek)} hrs</div>
          <div className="projector-outcome-unit">per week</div>
          <div className="projector-outcome-label">Time recaptured</div>
        </div>
        <div className="projector-outcome">
          <div className="projector-outcome-value projector-outcome-quality">{clientQuality}</div>
          <div className="projector-outcome-label">Client quality</div>
        </div>
        <div className="projector-outcome projector-outcome-primary">
          <div className="projector-outcome-value">+${cappedConservativeMonthly.toLocaleString()}</div>
          <div className="projector-outcome-unit">per month</div>
          <div className="projector-outcome-annual">+${cappedConservativeAnnual.toLocaleString()}/yr</div>
          <div className="projector-outcome-label">Revenue added <span className="projector-tag">top lever only</span></div>
        </div>
      </div>

      {/* Full potential callout */}
      <div className="projector-full-potential">
        <div className="projector-full-label">All pillars, 9-month ramp</div>
        <div className="projector-full-value">
          +${cappedFullMonthly.toLocaleString()}/mo &middot; +${cappedFullAnnual.toLocaleString()}/yr
        </div>
      </div>

      {/* ── #1 Lever ── */}
      {topLever && (
        <div className="projector-lever">
          <div className="projector-lever-eyebrow">Your #1 lever to pull first</div>
          <div className="projector-lever-name">{topLever.sectionLabel}</div>
          <p className="projector-lever-desc">{topLever.model.tooltip}</p>
          <div className="projector-lever-stats">
            <div className="projector-lever-stat">
              <div className="projector-lever-stat-value">+${Math.round(topLever.monthlyValue).toLocaleString()}/mo</div>
              <div className="projector-lever-stat-label">Expected impact</div>
            </div>
            <div className="projector-lever-stat">
              <div className="projector-lever-stat-value">{topLever.model.rampMonths} months</div>
              <div className="projector-lever-stat-label">Timeline to lift</div>
            </div>
            <div className="projector-lever-stat">
              <div className="projector-lever-stat-value" style={{ textTransform: 'capitalize' }}>{topLever.model.confidence}</div>
              <div className="projector-lever-stat-label">Data confidence</div>
            </div>
          </div>
        </div>
      )}

      {/* ── 12-month trajectory chart ── */}
      <div className="projector-chart">
        <div className="projector-chart-title">12-month revenue trajectory</div>
        <div className="projector-chart-area">
          <svg viewBox="0 0 480 160" preserveAspectRatio="none" className="projector-chart-svg">
            {/* Grid lines */}
            {[0.25, 0.5, 0.75].map(pct => (
              <line
                key={pct}
                x1="0" x2="480"
                y1={160 - pct * 160} y2={160 - pct * 160}
                stroke="currentColor" strokeOpacity="0.08" strokeDasharray="4 4"
              />
            ))}
            {/* Full potential area */}
            <path
              d={
                trajectoryData.map((d, i) => {
                  const x = (i / 12) * 480;
                  const y = 160 - ((d.full - chartMin) / chartRange) * 140 - 10;
                  return `${i === 0 ? 'M' : 'L'}${x},${y}`;
                }).join(' ') +
                ` L480,160 L0,160 Z`
              }
              fill="var(--color-primary)" fillOpacity="0.06"
            />
            {/* Full potential line */}
            <path
              d={trajectoryData.map((d, i) => {
                const x = (i / 12) * 480;
                const y = 160 - ((d.full - chartMin) / chartRange) * 140 - 10;
                return `${i === 0 ? 'M' : 'L'}${x},${y}`;
              }).join(' ')}
              fill="none" stroke="var(--color-primary)" strokeWidth="1.5" strokeOpacity="0.3" strokeDasharray="6 4"
            />
            {/* Conservative line */}
            <path
              d={trajectoryData.map((d, i) => {
                const x = (i / 12) * 480;
                const y = 160 - ((d.conservative - chartMin) / chartRange) * 140 - 10;
                return `${i === 0 ? 'M' : 'L'}${x},${y}`;
              }).join(' ')}
              fill="none" stroke="var(--color-primary)" strokeWidth="2.5"
            />
            {/* Start dot */}
            <circle
              cx="0"
              cy={160 - ((monthlyRevenue - chartMin) / chartRange) * 140 - 10}
              r="4" fill="var(--color-primary)"
            />
            {/* End dot (conservative) */}
            <circle
              cx="480"
              cy={160 - ((trajectoryData[12].conservative - chartMin) / chartRange) * 140 - 10}
              r="4" fill="var(--color-primary)"
            />
          </svg>
          {/* Y-axis labels */}
          <div className="projector-chart-y">
            <span>${Math.round(chartMax / 1000)}K</span>
            <span>${Math.round((chartMin + chartRange * 0.5) / 1000)}K</span>
            <span>${Math.round(chartMin / 1000)}K</span>
          </div>
          {/* X-axis */}
          <div className="projector-chart-x">
            <span>Now</span>
            <span>3 mo</span>
            <span>6 mo</span>
            <span>9 mo</span>
            <span>12 mo</span>
          </div>
        </div>
        <div className="projector-chart-legend">
          <span className="projector-chart-legend-item">
            <span className="projector-chart-legend-line solid" /> Top lever only
          </span>
          <span className="projector-chart-legend-item">
            <span className="projector-chart-legend-line dashed" /> All pillars (blended)
          </span>
        </div>
      </div>

      {/* ── Confidence footnotes ── */}
      <div className="projector-footnotes">
        <p>Projections based on data from {(43796).toLocaleString()} bookkeeper contacts across 5 survey sources. Conservative estimate shows top lever only at full ramp. Results vary based on implementation speed and market conditions.</p>
        {pillarData.slice(0, 3).map(p => (
          <p key={p.id}><strong>{p.sectionLabel}:</strong> {p.model.confidenceLabel}</p>
        ))}
      </div>
    </div>
  );
}
